import * as React from 'react';
import type { Row } from '@tanstack/react-table';

import type { SurfaceToken } from '../../components/ui/box-classes';
import type { DataTableDensity } from './data-table-types';

// ---------------------------------------------------------------------------
// Surface context — the surface the table paints on
//
// A table is spread across a dozen files: the header, the pinned cells, the
// row washes, the group nav, the editable cell and the drag veil all have to
// name the SAME surface or the table cuts a differently-colored hole in whatever
// it sits on. Threading a prop through all of them would be a dozen chances to
// forget one, so the surface travels the way density already does.
//
// Default `background`: a table is a page-level view unless told otherwise.
// ---------------------------------------------------------------------------

const DataTableSurfaceContext = React.createContext<SurfaceToken>('background');

function DataTableSurfaceProvider({
  surface,
  children,
}: {
  surface: SurfaceToken;
  children: React.ReactNode;
}) {
  return (
    <DataTableSurfaceContext.Provider value={surface}>
      {children}
    </DataTableSurfaceContext.Provider>
  );
}

function useDataTableSurface() {
  return React.useContext(DataTableSurfaceContext);
}

const DataTableDensityContext = React.createContext<DataTableDensity>('regular');

function DataTableDensityProvider({
  density,
  children,
}: {
  density: DataTableDensity;
  children: React.ReactNode;
}) {
  return (
    <DataTableDensityContext.Provider value={density}>
      {children}
    </DataTableDensityContext.Provider>
  );
}

function useDataTableDensity() {
  return React.useContext(DataTableDensityContext);
}

// ---------------------------------------------------------------------------
// Group rows context — lets the selection header scope to current group
// ---------------------------------------------------------------------------

 
const DataTableGroupRowsContext = React.createContext<Row<any>[] | null>(null);

function DataTableGroupRowsProvider<TData>({
  rows,
  children,
}: {
  rows: Row<TData>[];
  children: React.ReactNode;
}) {
  return (
    <DataTableGroupRowsContext.Provider value={rows}>
      {children}
    </DataTableGroupRowsContext.Provider>
  );
}

function useDataTableGroupRows<TData>(): Row<TData>[] | null {
  return React.useContext(DataTableGroupRowsContext);
}

// ---------------------------------------------------------------------------
// Shift-select context — tracks last selected row ID for range selection
// ---------------------------------------------------------------------------

const DataTableShiftSelectContext = React.createContext<React.RefObject<string | null> | null>(null);

function DataTableShiftSelectProvider({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<string | null>(null);
  return (
    <DataTableShiftSelectContext.Provider value={ref}>
      {children}
    </DataTableShiftSelectContext.Provider>
  );
}

function useDataTableShiftSelect() {
  return React.useContext(DataTableShiftSelectContext);
}

// ---------------------------------------------------------------------------
// Header drag context — lets the sortable header swallow the press that the
// pointer-up of a column-reorder drag would otherwise turn into a sort, and
// show a grab cursor on reorderable columns.
// ---------------------------------------------------------------------------

type DataTableHeaderDragValue = {
  didDragRef: React.RefObject<boolean>;
  reorderableIds: ReadonlySet<string>;
};

const DataTableHeaderDragContext =
  React.createContext<DataTableHeaderDragValue | null>(null);

function DataTableHeaderDragProvider({
  didDragRef,
  reorderableIds,
  children,
}: DataTableHeaderDragValue & {
  children: React.ReactNode;
}) {
  const value = React.useMemo(
    () => ({ didDragRef, reorderableIds }),
    [didDragRef, reorderableIds],
  );
  return (
    <DataTableHeaderDragContext.Provider value={value}>
      {children}
    </DataTableHeaderDragContext.Provider>
  );
}

function useDataTableHeaderDrag() {
  return React.useContext(DataTableHeaderDragContext);
}

// ---------------------------------------------------------------------------
// Add-column context — feeds the actions-column header's "+" picker
//
// The actions column is a static column DEFINITION (created once in the
// columns memo), but the set of hidden columns changes with every visibility
// edit. Carrying that set through context keeps the column definitions —
// and with them every per-group TanStack table — referentially stable across
// visibility changes; only the header's picker re-renders.
// ---------------------------------------------------------------------------

type DataTableAddColumnValue = {
  /** Hidden, addable columns in table order. */
  entries: readonly { id: string; label: string }[];
  /** Make the column visible. */
  onAdd: (columnId: string) => void;
};

const DataTableAddColumnContext =
  React.createContext<DataTableAddColumnValue | null>(null);

function DataTableAddColumnProvider({
  value,
  children,
}: {
  value: DataTableAddColumnValue | null;
  children: React.ReactNode;
}) {
  return (
    <DataTableAddColumnContext.Provider value={value}>
      {children}
    </DataTableAddColumnContext.Provider>
  );
}

function useDataTableAddColumn() {
  return React.useContext(DataTableAddColumnContext);
}

// ---------------------------------------------------------------------------
// Select-all context — lets header/cell read and toggle group select-all state
// ---------------------------------------------------------------------------

type DataTableSelectAllContextValue = {
  isSelectAll: boolean;
  setSelectAll: (value: boolean) => void;
};

const DataTableSelectAllContext = React.createContext<DataTableSelectAllContextValue>({
  isSelectAll: false,
  setSelectAll: () => {},
});

function DataTableSelectAllProvider({
  isSelectAll,
  setSelectAll,
  children,
}: DataTableSelectAllContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(
    () => ({ isSelectAll, setSelectAll }),
    [isSelectAll, setSelectAll],
  );
  return (
    <DataTableSelectAllContext.Provider value={value}>
      {children}
    </DataTableSelectAllContext.Provider>
  );
}

function useDataTableSelectAll() {
  return React.useContext(DataTableSelectAllContext);
}

export {
  DataTableAddColumnProvider,
  useDataTableAddColumn,
  DataTableSurfaceProvider,
  useDataTableSurface,
  DataTableDensityProvider,
  useDataTableDensity,
  DataTableGroupRowsProvider,
  useDataTableGroupRows,
  DataTableShiftSelectProvider,
  useDataTableShiftSelect,
  DataTableSelectAllProvider,
  useDataTableSelectAll,
  DataTableHeaderDragProvider,
  useDataTableHeaderDrag,
};
